/* -----------------Globals--------------- */
import { useEffect, useState } from "react";

import { parseAppConfig } from "@helpers/app-config";
import { isAndroid, isWeb } from "@helpers/platform";
import { retrieveKey } from "@memorang/helpers";
/* -----------------Helpers & Hooks--------------- */
import { useNavigationContainerRef } from "expo-router";

/* -----------------Child components--------------- */
import * as Font from "expo-font";
import * as SplashScreen from "expo-splash-screen";

import { mockedAiAssistant } from "@constants/demo";
import { mockedAppFavicon } from "@constants/demo";
import {
	isMockedData,
	mockedAppName,
	mockedCompanyUrl,
	mockedPrivacyUrl,
	mockedSupportEmail,
	mockedTosUrl,
} from "@constants/demo";
import { mockedTheme } from "@constants/demo/demo-themes";
import { ENDPOINT_APP_CONFIG } from "@constants/urls";
import { buildType } from "@helpers/expo-extras";
import { hubAxios } from "@helpers/lib/axios";
import i18n from "@helpers/lib/i18n";
import { reactNavigationIntegration } from "@helpers/lib/sentry";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { customFonts } from "@memorang/media";
import { getLocales } from "expo-localization";
/* -----------------Others--------------- */
import { OneSignal } from "../libs/onesignal";
import { useAppStore } from "./useAppStore";
import { useDemoExamStore } from "./useDemoExamStore";
import { useGlobalStore } from "./useGlobalStore";

export default function useCachedResources() {
	const [isLoadingComplete, setLoadingComplete] = useState(false);
	const [isDarkMode, setIsDarkMode] = useState(false);
	const oneSignalAppId = useAppStore((store) => store.app.keys.oneSignalAppId);
	const setAppConfig = useAppStore((store) => store.setAppConfig);
	const language = useGlobalStore((store) => store.language);
	const setLanguage = useGlobalStore((store) => store.setLanguage);

	const ref = useNavigationContainerRef();

	useEffect(() => {
		const locales = getLocales();
		if (!language) {
			const languages = Object.keys(i18n.services.resourceStore.data);
			const lang = languages.includes(locales[0].languageTag)
				? locales[0].languageTag
				: languages.find((lng) => lng.includes(locales[0].languageCode ?? ""));
			i18n.changeLanguage(lang);
			setLanguage(lang ?? "en-US");
		}
	}, [language, setLanguage]);

	useEffect(() => {
		if (ref && !isWeb) {
			reactNavigationIntegration.registerNavigationContainer(ref);
		}
	}, [ref]);

	const currentExam = useDemoExamStore((store) => store.currentExam);

	useEffect(() => {
		async function loadResourcesAndDataAsync() {
			try {
				SplashScreen.preventAutoHideAsync();
				// Load fonts
				await Font.loadAsync(customFonts);
				const darkMode = retrieveKey("isDarkMode");
				setIsDarkMode(darkMode as boolean);
				if (oneSignalAppId && OneSignal && isAndroid) {
					OneSignal.setAppId(oneSignalAppId);
					OneSignal.promptForPushNotificationsWithUserResponse();
				}
			} catch (e) {
				captureErrorToSentry(new Error("Error loading resources and data"), {
					error: JSON.stringify(e),
				});
			} finally {
				setLoadingComplete(true);
				SplashScreen.hideAsync();
			}
		}

		loadResourcesAndDataAsync();
	}, [oneSignalAppId]);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await hubAxios.get(ENDPOINT_APP_CONFIG(buildType));

				const parsedData = parseAppConfig(data);

				if (!parsedData) {
					throw new Error("No data found");
				}

				if (isMockedData) {
					parsedData.app.name = mockedAppName;
					parsedData.app.logo.default.light.uri = mockedAppFavicon;
					parsedData.app.logo.default.dark.uri = mockedAppFavicon;
					parsedData.app.logo.stacked.light.uri = mockedAppFavicon;
					parsedData.app.logo.stacked.dark.uri = mockedAppFavicon;
					parsedData.app.aiAssistant = mockedAiAssistant({
						examName: currentExam?.ailaName,
						examIcon: currentExam?.icon,
					});
					parsedData.app.links.tos = mockedTosUrl;
					parsedData.app.links.privacy = mockedPrivacyUrl;
					parsedData.app.links.companyUrl = mockedCompanyUrl;
					parsedData.supportEmail = mockedSupportEmail;
					parsedData.theme = mockedTheme;
					parsedData.app.font = "opensans";
					parsedData.app.componentOverrides = undefined;
				}

				setAppConfig(parsedData);
			} catch (error) {
				captureErrorToSentry(new Error("Error fetching custom app config"), {
					error: JSON.stringify(error),
				});
			}
		})();
	}, [setAppConfig, currentExam]);

	return {
		isLoadingComplete,
		isDarkMode,
	};
}
